
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { AutoCompleteComponent } from 'auto-complete-lib';
import { SectionType, TemplateUsage, Workflow } from 'enums-lib';
import { TemplateWorkflowConfig } from 'interfaces-lib';
import { combineLatest, EMPTY, filter, Observable, of, Subscription } from 'rxjs';
import { distinctUntilChanged, map, startWith, switchMap, tap } from 'rxjs/operators';
import { DefaultTemplateService } from './default-template.service';

@Component({
  selector: 'app-default-template',
  standalone: true,
  imports: [AutoCompleteComponent],
  templateUrl: './default-template.component.html',
  styleUrls: ['./default-template.component.scss'],
})
export class DefaultTemplateComponent implements OnInit, OnDestroy {
  @Input() section: FormGroup;
  @Input() entity: string;

  @ViewChild(AutoCompleteComponent) autoCompleteComponent: AutoCompleteComponent<any>;

  templates: TemplateWorkflowConfig[] = undefined;
  private subscriptions = new Subscription();

  constructor(private defaultTemplateService: DefaultTemplateService) {}

  get config(): FormGroup {
    return this.section.get('config') as FormGroup;
  }

  get defaultTemplateControl(): FormControl {
    return this.config.get('defaultTemplate') as FormControl;
  }

  get selectedName(): string | undefined {
    const id = this.defaultTemplateControl?.value;
    return this.templates.find((t) => t.id === id)?.name;
  }

  get noTemplatesAvailable() {
    return !this.templates?.length;
  }

  ngOnInit(): void {
    this.subscriptions.add(
      combineLatest([this.getAwesomeDocs(), this.templateFileTypesChange()])
        .pipe(filter(([templates]) => templates !== null))
        .subscribe(([templates, fileTypes]) => this.applyLoadedTemplates(templates, fileTypes)),
    );
  }

  private templateFileTypesChange(): Observable<string[]> {
    const templateFileTypesControl = this.config.get('templateFileTypes');
    return templateFileTypesControl.valueChanges.pipe(
      startWith(templateFileTypesControl.value),
      distinctUntilChanged(),
      tap(() => this.clearAutoComplete()),
    );
  }

  private getAwesomeDocs(): Observable<TemplateWorkflowConfig[]> {
    const isAwesomeDocAllowedForDocMerge = (t: TemplateWorkflowConfig) => {
      return t.isPublic
        && t.workflow === Workflow.SimpleDocx
        && t.usageTypes?.includes(TemplateUsage.DocMerge);
    };

    const templateEntityTypeControl = this.config.get('templateEntityType');
    return templateEntityTypeControl.valueChanges.pipe(
      startWith(templateEntityTypeControl.value),
      distinctUntilChanged(),
      tap(() => this.clearAutoComplete()),
      filter(entity => entity),
      switchMap((entityType) => this.defaultTemplateService.getTemplatesByEntity(entityType).pipe(
        map((templates) => templates.filter(isAwesomeDocAllowedForDocMerge)),
        startWith(null)
      ))
    );
  }

  private clearAutoComplete() {
    this.templates = undefined;
    this.autoCompleteComponent?.clearSelection();
  }

  private applyLoadedTemplates(templates: TemplateWorkflowConfig[], fileTypes: string[]): void {
    const hasFileTypeFilter = fileTypes?.length > 0;
    this.templates = hasFileTypeFilter
      ? templates.filter((t) => this.filetypeMatch(t, fileTypes))
      : templates;

    const selectedId = this.defaultTemplateControl?.value;
    if (selectedId && !this.templates.find((t) => t.id === selectedId)) {
      this.defaultTemplateControl?.setValue(null);
      this.defaultTemplateControl?.markAsDirty();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  filterFn = (value: string) => {
    const lower = (value || '').toLowerCase();
    const items = this.templates
      ?.filter((t) => t.name.toLowerCase().includes(lower))
      ?.map((t) => ({ label: t.name, data: t.id }))
      ?.sort((a, b) => a.label.localeCompare(b.label));
    return of(items);
  }

  onTemplateSelected(id: string): void {
    this.defaultTemplateControl?.setValue(id);
    this.defaultTemplateControl?.markAsDirty();
  }

  onInputCleared(): void {
    this.defaultTemplateControl?.setValue(null);
    this.defaultTemplateControl?.markAsDirty();
  }

  private filetypeMatch(template: TemplateWorkflowConfig, templateFileTypes: string[]): boolean {
    const outputSection = template.sections.find((s) => s.type === SectionType.OutputFileType);
    const destinationFileTypeMap = outputSection?.config?.destinations as { [entity: string]: string };
    if (!destinationFileTypeMap) {
      return false;
    }
    return templateFileTypes.includes(destinationFileTypeMap[template.entity]);
  }
}
